import { IconButton, Menu } from "@kaltura/ds-react-components";
import { MouseEvent, ReactElement, useState, useId } from "react";
import { MoreHorizontal24Icon } from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { getComponentFromPath, isMobileDevice, translate } from "@kaltura/mediaspace-shared-utils";
import { Analytics, ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { ErrorBoundary } from "../error-boundary/ErrorBoundary";

export interface ActionsMenuProps {
    items: KmsTypeComponent[];
    icon?: ReactElement;
    analyticsData: Analytics;
    onInvalidate?: () => void;
}

/**
 * actions menu (icon, with items like "edit", "delete" etc.)
 */
export const ActionsMenu = ({
    items,
    analyticsData,
    icon = <MoreHorizontal24Icon />,
    onInvalidate,
}: ActionsMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [itemState, setItemState] = useState<Record<string, unknown>[]>([]);
    const open = !!anchorEl;

    const uniqueId = useId();
    const buttonId = `actions-button_${uniqueId}`;
    const menuId = `actions-menu__${uniqueId}`;
    const close = () => setAnchorEl(null);

    const sendButtonAnalytics = useButtonAnalytics();

    const itemsToShow = items.filter((item) => (isMobileDevice ? !item.props.hideOnMobile : true));

    if (!itemsToShow.length) {
        return null;
    }

    /**
     * allow menu items to have menu level state.
     * its being sent back to the same menu item as props.
     */
    const handleItemStateChange = (itemStateId: number, key: string, value: unknown) => {
        setItemState((current) => ({
            ...current,
            [itemStateId]: { ...current[itemStateId], [key]: value },
        }));
    };

    const onMenuButtonClick = (ev: MouseEvent<HTMLElement>) => {
        setAnchorEl(ev.currentTarget);
        sendButtonAnalytics(analyticsData.action, ButtonClickAnalyticsType.MENU, analyticsData.mediaId);
    };

    return (
        <>
            <IconButton
                id={buttonId}
                variant={"borderless"}
                size={"medium"}
                color={"secondary"}
                onClick={onMenuButtonClick}
                onKeyDown={(ev) => {
                    if (ev.key === "ArrowDown" || ev.key === " ") {
                        setAnchorEl(ev.currentTarget);
                        sendButtonAnalytics(analyticsData.action, ButtonClickAnalyticsType.MENU, analyticsData.mediaId);
                    }
                }}
                aria-haspopup={true}
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={translate("show actions")}
            >
                {icon}
            </IconButton>

            <Menu
                id={menuId}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={open}
                onClose={close}
                MenuListProps={{
                    "aria-labelledby": buttonId,
                }}
            >
                {itemsToShow.map(({ component, props }, index) => {
                    // get relevant menu item state and pass it as props
                    const itemStateProps = itemState[index];
                    const Component = getComponentFromPath(component);

                    return (
                        <ErrorBoundary key={index} fallback={<></>}>
                            <Component
                                {...props}
                                {...itemStateProps}
                                onCloseMenu={close}
                                onItemStateChange={(key: string, value: unknown) =>
                                    handleItemStateChange(index, key, value)
                                }
                                autoFocus={index === 0}
                                onReload={onInvalidate}
                            />
                        </ErrorBoundary>
                    );
                })}
            </Menu>
        </>
    );
};
